import { DiagnosticError } from "./DiagnosticText";
import { diagnosticErrorText,formatDiagnostic } from "./diagnosticMessage";
import {diagnosticCatalog} from "./diagnosticCatalog";
import { type ReactNode, useEffect, useId, useRef } from "react";
import { APIError, type AuthSession, type IDSOperation } from "./api";
import { type Translator, useI18n } from "./i18n";
import {mailErrorText} from "./mailErrors";

export interface ManagementPageProps {
  session: AuthSession;
  refreshKey: number;
}

export function ManagementHeader({ eyebrow, title, description, status, inlineStatus }: {
  eyebrow: string; title: string; description: string; status?: ReactNode; inlineStatus?: ReactNode;
}) {
  return <header className="page-header"><div><span className="eyebrow page-eyebrow">{eyebrow}</span><h1>{title}{inlineStatus ? <> {inlineStatus}</> : null}</h1><p>{description}</p></div>{status}</header>;
}

export function ManagementMessages({ error, notice }: { error?: unknown; notice?: string }) {
  return <>{error ? <div className="global-error administrator-message" role="alert"><DiagnosticError error={error}/></div> : null}{notice ? <div className="success-message" role="status">{notice}</div> : null}</>;
}

export function ManagementPanel({ title, eyebrow, actions, children }: {
  title: string; eyebrow?: string; actions?: ReactNode; children: ReactNode;
}) {
  return <section className="panel"><div className="panel-header"><div>{eyebrow ? <span className="section-kicker">{eyebrow}</span> : null}<h2>{title}</h2></div>{actions}</div>{children}</section>;
}

export function ManagementTabs<T extends string>({ value, onChange, items }: {
  value: T; onChange: (value: T) => void; items: Array<{ value: T; label: string; count?: number }>;
}) {
  const { t } = useI18n();
  return <div className="page-tabs" role="tablist" aria-label={t("页面内容分区", "Page sections")}>{items.map(item => <button key={item.value} type="button" role="tab" aria-selected={value === item.value} className={value === item.value ? "page-tab page-tab-active" : "page-tab"} onClick={() => onChange(item.value)}><span>{item.label}</span>{item.count === undefined ? null : <small>{item.count}</small>}</button>)}</div>;
}

export function ManagementLoading() {
  const { t } = useI18n();
  return <div className="loading-state" role="status"><span className="loading-pulse"/><strong>{t("正在读取管理数据", "Loading management data")}</strong></div>;
}

export function ManagementEmpty({ children }: { children: ReactNode }) {
  return <div className="administrator-loading">{children}</div>;
}

export function ManagementOperations({ items }: { items: IDSOperation[] }) {
  const { t } = useI18n();
  return <ManagementPanel title={t("最近操作", "Recent operations")} eyebrow="AUDITED OPERATIONS">{items.length === 0 ? <ManagementEmpty>{t("尚无操作记录。", "No operations yet.")}</ManagementEmpty> : <div className="administrator-table-wrap"><table className="administrator-table"><thead><tr><th>{t("目标", "Target")}</th><th>{t("操作", "Action")}</th><th>{t("原因", "Reason")}</th><th>{t("状态", "State")}</th><th>{t("时间", "Time")}</th></tr></thead><tbody>{items.map(item => <tr key={item.operation_id}><td><strong>{item.target_id}</strong><span>{item.operation_id}</span></td><td>{item.action}</td><td>{item.reason}</td><td><span className={`user-state user-state-${item.state === "succeeded" ? "active" : "disabled"}`}>{item.state === "succeeded" ? t("成功", "Succeeded") : item.state === "failed" ? t("失败", "Failed") : t("处理中", "Pending")}</span>{item.error_code ? <span>{item.error_code}</span> : null}</td><td>{new Date(item.created_at).toLocaleString(t("zh-CN", "en-US"))}</td></tr>)}</tbody></table></div>}</ManagementPanel>;
}

export function ManagementPagination({ total, offset, limit, onChange, disabled }: {
  total: number; offset: number; limit: number; onChange: (offset: number) => void; disabled?: boolean;
}) {
  const { t } = useI18n();
  return <div className="directory-pagination" aria-label={t("分页", "Pagination")}><span>{t(`共 ${total} 条`, `${total} items`)}</span><div><button className="secondary-button" type="button" disabled={disabled || offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>{t("上一页", "Previous")}</button><strong>{Math.floor(offset / limit) + 1} / {Math.max(1, Math.ceil(total / limit))}</strong><button className="secondary-button" type="button" disabled={disabled || offset + limit >= total} onClick={() => onChange(offset + limit)}>{t("下一页", "Next")}</button></div></div>;
}

export function ManagementDialog({ title, eyebrow, onClose, disabled, children, variant = "dialog" }: {
  title: string; eyebrow?: string; onClose: () => void; disabled?: boolean; children: ReactNode; variant?: "dialog" | "drawer";
}) {
  const {t}=useI18n();
  const titleID = useId();
  const panel = useRef<HTMLElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    return () => previous?.focus();
  }, []);
  useEffect(()=>{if(variant!=="drawer")return;const previous=document.body.style.overflow;document.body.style.overflow="hidden";return()=>{document.body.style.overflow=previous}},[variant]);
  return <div className={`dialog-backdrop${variant==="drawer"?" management-drawer-backdrop":""}`} role="presentation"><section ref={panel} tabIndex={-1} className={`password-reset-dialog management-dialog${variant==="drawer"?" management-drawer":""}`} role="dialog" aria-modal="true" aria-labelledby={titleID} onKeyDown={event => {
    if (event.key === "Escape" && !disabled) { event.preventDefault(); onClose(); }
    if (event.key !== "Tab") return;
    const nodes = Array.from(panel.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]') ?? []).filter(node => node.offsetParent !== null);
    if (!nodes.length) { event.preventDefault(); return; }
    if (event.shiftKey && (document.activeElement === nodes[0] || document.activeElement === panel.current)) { event.preventDefault(); nodes[nodes.length - 1].focus(); }
    else if (!event.shiftKey && document.activeElement === nodes[nodes.length - 1]) { event.preventDefault(); nodes[0].focus(); }
  }}>{eyebrow ? <span className="section-kicker">{eyebrow}</span> : null}{variant==="drawer"?<div className="management-drawer-heading"><h2 id={titleID}>{title}</h2><button type="button" className="secondary-button" disabled={disabled} onClick={onClose}>{t("关闭","Close")}</button></div>:<h2 id={titleID}>{title}</h2>}{children}</section></div>;
}

export function ManagementDrawer(props: Omit<Parameters<typeof ManagementDialog>[0],"variant">) {
  return <ManagementDialog {...props} variant="drawer"/>;
}

export function managementError(error: unknown, t: Translator): string {
  if (error instanceof APIError) {
	if(diagnosticCatalog[error.diagnostic.code])return formatDiagnostic(error.diagnostic,t);
    const mailMessage=mailErrorText(error.diagnostic.code,t,typeof error.diagnostic.params?.smtp_code === "number" ? error.diagnostic.params.smtp_code : 0);
    if(mailMessage)return mailMessage;
    if (error.status === 401) return t("登录已过期，请刷新页面后重新登录。", "Your session expired. Refresh and sign in again.");
    if (error.status === 409) return t("配置或状态已被其他操作更新，请刷新后重新核对并提交。", "The configuration or state changed. Refresh, review, and submit again.");
    if (error.status === 403) return t("当前操作未获得管理接口授权，请检查服务权限。", "This operation is not authorized. Check the management service permissions.");
    // IDS password policy codes are forwarded unchanged by Control Center.
    if (error.code === "PASSWORD_TOO_SHORT") return t("密码至少需要 8 个字符。", "The password requires at least 8 characters.");
    if (error.code === "PASSWORD_TOO_WEAK") return t("这个密码太常见或太有规律，容易被猜到，请换一个。", "This password is too common or predictable. Choose another one.");
    if (error.code === "PASSWORD_TOO_LONG") return t("密码过长。", "The password is too long.");
    return diagnosticErrorText(error,t);
  }
  return diagnosticErrorText(error,t);
}
